import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Local, hydration-safe replacement for `next-themes`.
 *
 * Why this exists:
 *  - `next-themes`' ThemeProvider injects an inline <script> as its first
 *    child. When the marketing routes are prerendered, that script appears as
 *    the first child of `#root`. React's first client render does not
 *    reproduce it, causing hydration errors #418 / #423.
 *
 * This provider renders `{children}` only — no script, no wrapper node — and
 * relies on a tiny inline bootstrap in `index.html <head>` to set the
 * `light` / `dark` class on `<html>` before first paint.
 */

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  themes: Theme[];
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

function safeGetStoredTheme(): Theme | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
    return null;
  } catch {
    return null;
  }
}

function safeSetStoredTheme(value: Theme) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
}

function safeGetSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "light";
  }
  try {
    return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

function applyHtmlClass(resolved: ResolvedTheme) {
  if (typeof document === "undefined") return;
  try {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(resolved);
  } catch {
    /* ignore */
  }
}

interface ThemeProviderProps {
  children: ReactNode;
  /** Default theme used for the first deterministic render. */
  defaultTheme?: Theme;
  /**
   * Accepted for API parity with `next-themes` — ignored.
   * This provider always toggles the `light` / `dark` class on `<html>`.
   */
  attribute?: string | string[];
  /** Accepted for API parity — ignored (system support is always on). */
  enableSystem?: boolean;
  /** Accepted for API parity — ignored. */
  enableColorScheme?: boolean;
  /** Accepted for API parity — ignored. */
  disableTransitionOnChange?: boolean;
  /** Accepted for API parity — ignored. */
  storageKey?: string;
  /** Accepted for API parity — ignored. */
  themes?: string[];
  /** Accepted for API parity — ignored. */
  forcedTheme?: string;
  /** Accepted for API parity — ignored. */
  value?: Record<string, string>;
}

export function ThemeProvider({
  children,
  defaultTheme = "light",
}: ThemeProviderProps) {
  // Deterministic first render: matches the prerendered HTML which was
  // produced with `defaultTheme`. Stored / system values are reconciled in
  // a post-mount effect below.
  const [theme, setThemeState] = useState<Theme>(defaultTheme);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(
    defaultTheme === "dark" ? "dark" : "light",
  );

  // After mount: hydrate from storage (or system).
  useEffect(() => {
    const stored = safeGetStoredTheme();
    if (stored && stored !== theme) {
      setThemeState(stored);
    }
    // No deps: run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve + apply whenever `theme` changes; subscribe to system changes
  // only while `theme === 'system'`.
  useEffect(() => {
    if (theme === "system") {
      const sys = safeGetSystemTheme();
      setResolvedTheme(sys);
      applyHtmlClass(sys);

      if (
        typeof window === "undefined" ||
        typeof window.matchMedia !== "function"
      ) {
        return;
      }
      let mql: MediaQueryList;
      try {
        mql = window.matchMedia(DARK_QUERY);
      } catch {
        return;
      }
      const handler = (e: MediaQueryListEvent) => {
        const next: ResolvedTheme = e.matches ? "dark" : "light";
        setResolvedTheme(next);
        applyHtmlClass(next);
      };
      try {
        mql.addEventListener("change", handler);
        return () => {
          try {
            mql.removeEventListener("change", handler);
          } catch {
            /* ignore */
          }
        };
      } catch {
        // Safari < 14
        try {
          (mql as unknown as { addListener: (h: (e: MediaQueryListEvent) => void) => void }).addListener(handler);
          return () => {
            try {
              (mql as unknown as { removeListener: (h: (e: MediaQueryListEvent) => void) => void }).removeListener(handler);
            } catch {
              /* ignore */
            }
          };
        } catch {
          return;
        }
      }
    }

    const next: ResolvedTheme = theme === "dark" ? "dark" : "light";
    setResolvedTheme(next);
    applyHtmlClass(next);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    safeSetStoredTheme(next);
    setThemeState(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      resolvedTheme,
      setTheme,
      themes: ["light", "dark", "system"],
    }),
    [theme, resolvedTheme, setTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx;
  // Safe fallback if a consumer mounts outside the provider — keeps API
  // calls non-throwing, matching `next-themes`' permissive behaviour.
  return {
    theme: "light",
    resolvedTheme: "light",
    setTheme: () => {
      /* no-op */
    },
    themes: ["light", "dark", "system"],
  };
}